import { Injectable, Optional } from '@angular/core';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, first } from 'rxjs/operators';
import * as _ from 'lodash';
import * as fromApp from '../../store/app.reducer';
import { ProductDetailsComponent } from 'src/app/product-details/product-details.component';
import { ProductDetailsOperationsService } from 'src/app/product-details/product-details-operations.service';
import { FilterOnlyDineInDeliveryPickupProductsPipe } from 'src/app/catalog/filter-only-dine-in-delivery-pickup-products.pipe';
const productDisplayName = (line, _showSecondary?: boolean) => line?.name || '';
import {
  BundleOfferPromptComponent,
  BundleOfferPromptView,
} from 'src/app/popups/bundle-offer-prompt/bundle-offer-prompt.component';

// Contract: dm-mobileorder-brain docs/05-catalog-pricing-availability.md
export const BUNDLE_OFFER_CONTRACT_VERSION = 1;
export const BUNDLE_OFFER_MAX_AUTOMATIC_PROMPTS = 2;

/** Ordinary consumer ordering only; each offer also needs merchant consent. */
export function bundleOfferSuggestionsEnabled(user: any): boolean {
  return !user?.uposMoUser && !user?.catalogZeroPricesMode;
}

export interface BundleOfferPricingContext {
  // A gift or classic-offer Product Details opened by the same response wins.
  productDetailsOpen: boolean;
  // The offer-activated animation or a campaign / promotion-not-found alert of
  // the same response opens after this decision, so getTop cannot see it.
  promotionNoticeOpening?: boolean;
  // The complete-order pricing call: checkout moves on from its response.
  checkoutInProgress?: boolean;
  minimumOrder?: number;
  minimumOrderSubtotal?: number;
}

/** Owns WHETHER a bundle-offer suggestion is shown (capability, catalog check,
 * exposure and overlay arbitration). HOW it is shown lives only in
 * BundleOfferPromptComponent, so a non-blocking cart note can replace the modal
 * without touching this decision, the calculator or the contract. */
@Injectable({
  providedIn: 'root',
})
export class BundleOfferSuggestionService {
  // Exposure is per cart session: it resets when the cart becomes empty, which
  // a completed or cleared cart does too.
  private shownSignatures: string[] = [];
  private automaticPrompts = 0;
  private promptOpen = false;
  private promptGuard: string = null;
  private promptModal: any = null;
  private acceptedOffer: {
    offerProductId: string;
    knownOfferCartIds: string[];
    coveredItems: BundleOfferPromptView['coveredItems'];
    coveredUnits: { cartId: string; selection: string; units: number; quantity: number }[];
  } = null;
  // The catalog page clears its categories when it is left, so the cart page
  // decides against the last catalog the customer saw for this store/language.
  private catalogSnapshot: {
    storeId: string;
    language: string;
    categories: any[];
  } = null;
  private subscriptions: Subscription[] = [];
  private sessionStoreId: string;
  private pendingCoveredUnits: { cartId: string; selection: string; units: number; quantity: number }[] = [];

  constructor(
    private store: Store<fromApp.AppState>,
    private modalController: ModalController,
    private toastController: ToastController,
    private translateService: TranslateService,
    private productDetailsOperationsService: ProductDetailsOperationsService,
    @Optional() private alertController?: AlertController
  ) {}

  /** Request fields for a cart-pricing call; never for create-order. */
  requestCapability(): { bundleOfferContractVersion?: number } {
    if (!bundleOfferSuggestionsEnabled(this.readState('auth')?.user)) {
      return {};
    }

    this.watchState();
    return { bundleOfferContractVersion: BUNDLE_OFFER_CONTRACT_VERSION };
  }

  // Identity includes customer selections but excludes prices refreshed by the quote.
  captureQuoteContext(): string {
    return JSON.stringify([
      this.currentStoreAccount()?.store_id,
      this.readState('groupApp')?.selectedLangugage,
      _.pick(this.readState('auth')?.user, ['customer_id', 'user_id', 'group', 'uposMoUser', 'catalogZeroPricesMode']),
      _.pick(this.readState('deliveryMethodAddress'), ['orderPickup', 'dineIn', 'selectedDineInTable', 'selectedAddress', 'selectedPreorderDate']),
      _.map(this.readState('cart')?.products, line => ({ quantity: line.quantity, selection: this.lineSelection(line) })),
      _.map(this.readState('cartActiveCoupons')?.activeCoupons, coupon => _.pick(coupon, ['timestamp', 'coupon_id', 'couponCheckbox', 'quantity'])),
      this.readState('cartCustomerOptions')?.cartCustomerOptions,
      _.map(this.readState('checkboxPromotions')?.checkboxPromotions, promo => _.pick(promo, ['id', 'promotion_id', 'promo_id', 'promotionId', 'timestamp', 'promoCheckbox', 'checked', 'custom_discount', 'discountType', 'discountPercentage', 'fixedAmount'])),
    ]);
  }

  private lineSelection(line): string {
    const product = (item, nested = false) => ({
      ..._.pick(item, ['cart_id', 'product_id', 'comments']),
      ...(nested ? {quantity: item.quantity} : {}),
      options: _.map(item?.options, option => ({
        ..._.pick(option, ['option_id', 'value', 'fieldValue', 'text']),
        choices: _.map(_.filter(option?.choices, choice => choice?.is_preselected), choice => ({
          ..._.pick(choice, ['choice_id', 'quantity']),
          bundled_product: choice.bundled_product ? product(choice.bundled_product, true) : null,
        })),
      })),
    });
    return JSON.stringify(product(line));
  }

  filterPricingResponse(res: any, quoteContext: string): any {
    if (!quoteContext || quoteContext === this.captureQuoteContext()) return res;
    return _.omit(res, ['bundle_offer_contract_version', 'bundle_offer_suggestions']);
  }

  /** Called once per successful pricing response, before the recommended
   * products modal. Returns true when the offer prompt owns the overlay of this
   * response, so the caller must skip the recommended products modal. */
  handlePricingResponse(res: any, context: BundleOfferPricingContext): boolean {
    if (this.promptOpen) {
      return true;
    }

    const suggestions = res?.bundle_offer_suggestions;
    if (
      res?.bundle_offer_contract_version !== BUNDLE_OFFER_CONTRACT_VERSION ||
      !_.isArray(suggestions) ||
      !suggestions.length ||
      !bundleOfferSuggestionsEnabled(this.readState('auth')?.user)
    ) {
      return false;
    }

    this.watchState();

    // Yielding does not spend the signature: a later quote can still prompt.
    if (
      context?.productDetailsOpen ||
      context?.promotionNoticeOpening ||
      context?.checkoutInProgress
    ) {
      return false;
    }

    this.reconcilePendingUnits(this.readState('cart')?.products);
    const suggestion = _.find(suggestions, candidate => !this.coversPendingUnits(candidate));
    if (!this.canPromptAutomatically(suggestion?.signature)) {
      return false;
    }

    const view = this.buildPromptView(suggestion, res.cart);
    if (!view || (Number(context.minimumOrder) > 0 &&
      !(Number(context.minimumOrderSubtotal) - Number(suggestion.replaced_total) +
        (view.offerProduct.do_not_include_in_min_order ? 0 : Number(suggestion.offer_total)) >= Number(context.minimumOrder)))) {
      return false;
    }

    this.promptOpen = true;
    this.promptGuard = this.captureQuoteContext();
    this.presentPrompt(view, this.promptGuard);
    return true;
  }

  private canPromptAutomatically(signature: any) {
    return (
      typeof signature === 'string' &&
      !!signature &&
      !_.includes(this.shownSignatures, signature) &&
      this.automaticPrompts < BUNDLE_OFFER_MAX_AUTOMATIC_PROMPTS
    );
  }

  private buildPromptView(suggestion, cart): BundleOfferPromptView {
    if (
      !suggestion.offer_product_id ||
      !_.isArray(suggestion.covered) ||
      !suggestion.covered.length ||
      !(parseFloat(suggestion.saving) > 0) ||
      !_.isFinite(parseFloat(suggestion.offer_total))
    ) {
      return null;
    }

    const offerProduct = this.findVisibleOfferProduct(suggestion);
    if (!offerProduct) {
      return null;
    }

    const storeAccount = this.currentStoreAccount();
    const coveredItems = _.map(suggestion.covered, (covered) => {
      const line = _.find(
        cart,
        (cartProduct) =>
          cartProduct &&
          _.toString(cartProduct.cart_id) === _.toString(covered?.cart_id)
      );
      if (!line || !Number.isInteger(covered.units) || covered.units < 1 || covered.units > Number(line.quantity) || _.toString(line.product_id) !== _.toString(covered.product_id)) {
        return null;
      }
      const name = productDisplayName(
        line,
        false
      );
      return {
        cart_id: line.cart_id,
        units: covered.units,
        label: covered.units > 1 || Number(line.quantity) > covered.units ? covered.units + 'x ' + name : name,
      };
    });

    // A covered line that is no longer in the cart makes the suggestion stale.
    if (_.some(coveredItems, (item) => !item)) {
      return null;
    }

    return {
      signature: suggestion.signature,
      offerProduct,
      coveredItems,
      offerTotal: suggestion.offer_total,
      showPricesWithComma: storeAccount?.showPricesWithComma === true,
      currencySymbol: storeAccount?.currencySymbol || '€',
    };
  }

  private findVisibleOfferProduct(suggestion) {
    const categories = this.catalogCategories();
    const user = this.readState('auth')?.user;
    const delivery = this.readState('deliveryMethodAddress');
    const allowedCategories =
      user?.displayOnlyUserCategories && user.userCategoriesIds?.length
        ? user.userCategoriesIds
        : null;
    const offerProductId = _.toString(suggestion.offer_product_id);
    // The offer's own category first; a product can also be listed elsewhere.
    const orderedCategories = _.sortBy(categories, (category) =>
      _.toString(category?.category_id) ===
      _.toString(suggestion.offer_category_id)
        ? 0
        : 1
    );

    let offerProduct = null;
    _.each(orderedCategories, (category) => {
      if (
        !category ||
        !category.activeTotal ||
        !category.availableTotal ||
        category.hidden ||
        category.showOnlyInKioskApp ||
        category.showOnlyInQRCatalog ||
        (allowedCategories &&
          !_.includes(allowedCategories, category.category_id))
      ) {
        return;
      }

      const product = _.find(
        category.products,
        (prd) => prd && _.toString(prd.product_id) === offerProductId
      );
      if (product && this.isVisibleOfferProduct(product, delivery)) {
        offerProduct = product;
        return false;
      }
    });

    return offerProduct;
  }

  // The recommended products popup filter, plus availability and the
  // fulfilment-method filter of the catalog grid and calculator.
  private isVisibleOfferProduct(product, delivery) {
    return !!(
      product.bundle &&
      product.mobileorderBundleOfferSuggestions?.enabled === true &&
      product.mobileorderBundleOfferSuggestions.store_id === this.currentStoreAccount()?.store_id &&
      product.activeTotal &&
      product.availableTotal &&
      !product.hidden &&
      !product.showOnlyInQRCatalog &&
      !product.showOnlyInKioskApp &&
      (!(product.delivery || product.pickup || product.dinein) || !!product[delivery?.orderPickup ? "pickup" : delivery?.dineIn ? "dinein" : "delivery"]) &&
      product.product_categories_active_check &&
      product.product_categories_availability_check !== false &&
      new FilterOnlyDineInDeliveryPickupProductsPipe().transform(
        [product],
        delivery?.orderPickup,
        delivery?.dineIn
      ).length
    );
  }

  private catalogCategories(): any[] {
    const snapshot = this.catalogSnapshot;
    if (
      snapshot &&
      snapshot.storeId ===
        this.currentStoreAccount()?.store_id &&
      snapshot.language === this.readState('groupApp')?.selectedLangugage
    ) {
      return snapshot.categories;
    }
    return [];
  }

  private async presentPrompt(view: BundleOfferPromptView, guard: string) {
    const current = () => guard === this.captureQuoteContext() && !!this.findVisibleOfferProduct({offer_product_id: view.offerProduct.product_id});
    let modal: any;
    let presented = false;
    try {
      // Alerts and modals both own the overlay. Yielding spends no exposure.
      if (await this.modalController.getTop() || await this.alertController?.getTop() || !current()) return;
      modal = await this.modalController.create({
        component: BundleOfferPromptComponent,
        cssClass: 'bundle-offer-prompt-modal',
        animated: true,
        backdropDismiss: false,
        componentProps: { view },
      });
      const top = await this.modalController.getTop();
      if (!current() || (top && top !== modal) || await this.alertController?.getTop()) return;
      this.promptModal = modal;
      await modal.present();
      presented = true;
      if (!current()) {
        await modal.dismiss();
        return;
      }
      this.shownSignatures.push(view.signature);
      this.automaticPrompts++;
      const { data } = await modal.onDidDismiss();
      this.promptModal = null;
      this.promptGuard = null;
      if (data === 'accept' && current()) await this.acceptOffer(view);
    } catch (err) {
      if (presented) await modal?.dismiss();
      console.log('bundle offer prompt error', err);
    } finally {
      // Ionic dismiss() does not remove an overlay that has never presented.
      if (modal && !presented) modal.remove();
      this.promptModal = null;
      this.promptGuard = null;
      this.promptOpen = false;
    }
  }

  private async acceptOffer(view: BundleOfferPromptView) {
    const offerProductId = _.toString(view.offerProduct.product_id);
    const accepted = {
      offerProductId,
      knownOfferCartIds: this.offerCartIds(
        this.readState('cart')?.products,
        offerProductId
      ),
      coveredItems: view.coveredItems,
      coveredUnits: _.map(view.coveredItems, item => {
        const line = _.find(this.readState('cart')?.products, product => _.toString(product.cart_id) === _.toString(item.cart_id));
        return { cartId: _.toString(item.cart_id), selection: this.lineSelection(line), units: item.units, quantity: Number(line?.quantity) };
      }),
    };
    this.acceptedOffer = accepted;
    try {
      await this.presentOfferProductDetails(view.offerProduct);
    } finally {
      // Product Details adds the offer line before it closes, so the reminder
      // has already fired when the offer was added. A close without adding
      // disarms it: a later, unrelated offer line must not trigger it.
      if (this.acceptedOffer === accepted) {
        this.acceptedOffer = null;
      }
    }
  }

  // Same path as a recommended product with options: Product Details opens the
  // offer as a NEW product and the customer picks its items there.
  private async presentOfferProductDetails(product) {
    const productDetailsModal = await this.modalController.create({
      component: ProductDetailsComponent,
      cssClass:
        this.productDetailsOperationsService.getProductDetailsModalCssClass(product, this.readState('windowInfo')?.windowDimensions),
      animated: true,
      componentProps: {
        product_id: product.product_id,
      },
      backdropDismiss: false,
    });

    await productDetailsModal.present();
    await productDetailsModal.onDidDismiss();
  }

  private offerCartIds(products, offerProductId: string): string[] {
    return _.map(
      _.filter(
        products,
        (line) => line && _.toString(line.product_id) === offerProductId
      ),
      (line) => _.toString(line.cart_id)
    );
  }

  private watchState() {
    if (this.subscriptions.length) {
      return;
    }

    this.sessionStoreId = this.currentStoreAccount()?.store_id;
    this.subscriptions.push(
      combineLatest([
        this.store.select('catalog'),
        this.store.select('hyperCategories'),
      ]).subscribe(([catalogState, hyperCategoriesState]) => {
        const categories =
          hyperCategoriesState?.hyperCategories?.length &&
          hyperCategoriesState.allCategories?.length
            ? hyperCategoriesState.allCategories
            : catalogState?.allCategories;
        if (_.isArray(categories) && categories.length) {
          this.catalogSnapshot = {
            storeId: this.currentStoreAccount()?.store_id,
            language: this.readState('groupApp')?.selectedLangugage,
            categories,
          };
        }
      })
    );

    this.subscriptions.push(
      combineLatest([this.store.select('currentStore'), this.store.select('groupApp'), this.store.select('auth'), this.store.select('deliveryMethodAddress'), this.store.select('cartActiveCoupons'), this.store.select('cartCustomerOptions'), this.store.select('checkboxPromotions'), this.store.select('stores')])
        .subscribe(() => {
          const storeId = this.currentStoreAccount()?.store_id;
          const storeChanged = this.sessionStoreId !== storeId;
          const languageChanged = this.catalogSnapshot && this.catalogSnapshot.language !== this.readState('groupApp')?.selectedLangugage;
          if (storeChanged || languageChanged) {
            this.catalogSnapshot = null;
            this.acceptedOffer = null;
          }
          if (storeChanged || languageChanged) {
            this.shownSignatures = [];
            this.automaticPrompts = 0;
          }
          if (storeChanged) this.pendingCoveredUnits = [];
          this.sessionStoreId = storeId;
          this.dismissStalePrompt();
        })
    );
    this.subscriptions.push(
      this.store
        .select('cart')
        .pipe(distinctUntilChanged())
        .subscribe((state) => this.onCartProducts(state?.products))
    );
  }

  private dismissStalePrompt() {
    if (this.promptGuard && this.promptGuard !== this.captureQuoteContext()) {
      this.promptModal?.dismiss();
    }
  }

  private onCartProducts(products) {
    this.dismissStalePrompt();
    this.reconcilePendingUnits(products);
    if (!products?.length) {
      this.shownSignatures = [];
      this.automaticPrompts = 0;
      this.acceptedOffer = null;
      return;
    }

    const accepted = this.acceptedOffer;
    if (
      !accepted ||
      !_.difference(
        this.offerCartIds(products, accepted.offerProductId),
        accepted.knownOfferCartIds
      ).length
    ) {
      return;
    }

    // The offer line reached the cart. Nothing is removed automatically: remind
    // the customer about the covered items that are still there.
    this.acceptedOffer = null;
    this.pendingCoveredUnits.push(...accepted.coveredUnits);
    this.reconcilePendingUnits(products);
    const cartIds = _.map(products, (line) => _.toString(line?.cart_id));
    const labels = _.map(
      _.filter(accepted.coveredItems, (item) =>
        _.includes(cartIds, _.toString(item.cart_id))
      ),
      'label'
    );
    if (labels.length) {
      this.showRemoveCoveredItemsToast(labels);
    }
  }

  private reconcilePendingUnits(products) {
    this.pendingCoveredUnits = _.compact(_.map(this.pendingCoveredUnits, pending => {
      const line = _.find(products, item => _.toString(item?.cart_id) === pending.cartId);
      if (!line || this.lineSelection(line) !== pending.selection) return null;
      const quantity = Number(line.quantity);
      const units = Math.max(0, pending.units - Math.max(0, pending.quantity - quantity));
      return units > 0 ? { ...pending, units, quantity } : null;
    }));
    this.pendingCoveredUnits = _.map(_.groupBy(this.pendingCoveredUnits, 'cartId'), entries => ({
      ...entries[0], units: Math.min(entries[0].quantity, _.sumBy(entries, 'units')),
    }));
  }

  private coversPendingUnits(suggestion): boolean {
    return _.some(suggestion?.covered, covered => {
      const line = _.find(this.readState('cart')?.products, item => _.toString(item?.cart_id) === _.toString(covered.cart_id));
      const reserved = _.sumBy(_.filter(this.pendingCoveredUnits, pending => pending.cartId === _.toString(covered.cart_id)), 'units');
      return reserved > 0 && Number(covered.units) > Math.max(0, Number(line?.quantity) - reserved);
    });
  }

  private async showRemoveCoveredItemsToast(labels: string[]) {
    const toast = await this.toastController.create({
      message: this.translateService.instant(
        'bundleOfferPrompt.removeCoveredItems',
        { items: labels.join(', ') }
      ),
      duration: 6000,
      position: 'middle',
      cssClass: 'bundle-offer-reminder-toast',
      buttons: [
        {
          cssClass: 'bundle-offer-reminder-close',
          side: 'end',
          icon: 'assets/ionicons/close.svg',
          handler: () => {},
        },
      ],
    });

    toast.present();
  }

  private currentStoreAccount(): any {
    return _.find(this.readState('stores')?.stores, {store_id: this.readState('currentStore')?.currentStoreId});
  }

  private readState<K extends keyof fromApp.AppState>(
    key: K
  ): fromApp.AppState[K] {
    let value: fromApp.AppState[K];
    this.store
      .select(key)
      .pipe(first())
      .subscribe((state) => {
        value = state;
      });
    return value;
  }
}
